
import { useState, useRef } from 'react';

interface DetailedAnalysisProps {
  selectedMetroRegion: string;
  selectedBasicRegions: string[];
  selectedCategory: string;
}

export default function DetailedAnalysis({
  selectedMetroRegion,
  selectedBasicRegions,
  selectedCategory
}: DetailedAnalysisProps) {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const categories = [
    {
      id: 'legal',
      name: '법규환경',
      score: 88,
      grade: 'A',
      change: '+2',
      icon: 'ri-book-line',
      color: 'blue',
      details: [
        { name: '법규 명확성', score: 92, trend: 'up' },
        { name: '규제 일관성', score: 85, trend: 'up' },
        { name: '법규 접근성', score: 87, trend: 'stable' }
      ],
      notes: '법규 명확성이 크게 개선되었으며, 규제 일관성도 향상되고 있습니다.'
    },
    {
      id: 'complaint',
      name: '민원규제',
      score: 82,
      grade: 'B',
      change: '+1',
      icon: 'ri-customer-service-line',
      color: 'green',
      details: [
        { name: '민원 처리 속도', score: 80, trend: 'up' },
        { name: '민원 만족도', score: 84, trend: 'up' },
        { name: '민원 투명성', score: 82, trend: 'stable' }
      ],
      notes: '민원 처리 속도와 만족도가 개선되고 있습니다.'
    },
    {
      id: 'economic',
      name: '경제활동친화성',
      score: 85,
      grade: 'B',
      change: '+3',
      icon: 'ri-line-chart-line',
      color: 'purple',
      details: [
        { name: '창업 용이성', score: 88, trend: 'up' },
        { name: '사업 운영 편의성', score: 83, trend: 'up' },
        { name: '규제 합리성', score: 84, trend: 'up' }
      ],
      notes: '창업 용이성이 크게 향상되었으며, 전반적인 경제활동 환경이 개선되고 있습니다.'
    },
    {
      id: 'procedure',
      name: '행정절차',
      score: 79,
      grade: 'C',
      change: '0',
      icon: 'ri-file-list-line',
      color: 'orange',
      details: [
        { name: '절차 간소화', score: 75, trend: 'stable' },
        { name: '처리 기간', score: 81, trend: 'stable' },
        { name: '온라인 서비스', score: 82, trend: 'up' }
      ],
      notes: '온라인 서비스는 개선되고 있으나, 절차 간소화가 필요합니다.'
    },
    {
      id: 'permit',
      name: '인허가',
      score: 76,
      grade: 'C',
      change: '-1',
      icon: 'ri-file-shield-line',
      color: 'red',
      details: [
        { name: '인허가 기간', score: 74, trend: 'down' },
        { name: '인허가 투명성', score: 78, trend: 'stable' },
        { name: '인허가 예측가능성', score: 76, trend: 'stable' }
      ],
      notes: '인허가 기간이 다소 증가하여 개선이 필요합니다.'
    },
    {
      id: 'tax',
      name: '세무환경',
      score: 90,
      grade: 'A',
      change: '+2',
      icon: 'ri-money-dollar-circle-line',
      color: 'teal',
      details: [
        { name: '세무 투명성', score: 92, trend: 'up' },
        { name: '세무 지원', score: 89, trend: 'up' },
        { name: '세무 편의성', score: 89, trend: 'stable' }
      ],
      notes: '세무 투명성과 지원이 우수한 수준을 유지하고 있습니다.'
    }
  ];

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = direction === 'left' 
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
          <i className="ri-bar-chart-box-line text-blue-600 mr-2"></i>
          평가항목별 상세분석
        </h3>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => scroll('left')}
            className="hidden sm:flex p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-left-s-line text-gray-600"></i>
          </button>
          <button
            onClick={() => scroll('right')}
            className="hidden sm:flex p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-right-s-line text-gray-600"></i>
          </button>
        </div>
      </div>

      <div 
        ref={scrollContainerRef}
        className="flex overflow-x-auto space-x-3 sm:space-x-4 pb-4 -mx-3 px-3 sm:mx-0 sm:px-0 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {categories.map((category) => (
          <div
            key={category.id}
            className="flex-shrink-0 w-[280px] sm:w-[320px] bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* 카드 헤더 */}
            <div 
              className={`bg-${category.color}-50 p-3 sm:p-4 cursor-pointer`}
              onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-${category.color}-100 rounded-lg flex items-center justify-center`}>
                    <i className={`${category.icon} text-${category.color}-600 text-base sm:text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-base font-semibold text-gray-900 whitespace-nowrap">{category.name}</h4>
                    <span className={`text-xs font-medium ${
                      category.grade === 'A' ? 'text-green-600' :
                      category.grade === 'B' ? 'text-blue-600' :
                      'text-yellow-600'
                    }`}>
                      {category.grade}등급
                    </span>
                  </div>
                </div>
                <i className={`ri-arrow-${expandedCategory === category.id ? 'up' : 'down'}-s-line text-gray-400`}></i>
              </div>
              
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">{category.score}</div>
                  <div className="text-xs text-gray-600">점</div>
                </div>
                <div className={`flex items-center text-xs sm:text-sm font-medium ${
                  category.change.startsWith('+') ? 'text-green-600' :
                  category.change.startsWith('-') ? 'text-red-600' :
                  'text-gray-600'
                }`}>
                  {category.change.startsWith('+') && <i className="ri-arrow-up-line mr-1"></i>}
                  {category.change.startsWith('-') && <i className="ri-arrow-down-line mr-1"></i>}
                  {category.change === '0' && <i className="ri-subtract-line mr-1"></i>}
                  {category.change}
                </div>
              </div>
            </div>

            {/* 카드 상세 내용 */}
            {expandedCategory === category.id && (
              <div className="p-3 sm:p-4 border-t border-gray-200">
                <div className="space-y-3">
                  <div>
                    <h5 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">세부 항목 점수</h5>
                    <div className="space-y-2">
                      {category.details.map((detail, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <span className="text-xs text-gray-600 truncate pr-2">{detail.name}</span>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <span className="text-xs font-medium text-gray-900">{detail.score}</span>
                            {detail.trend === 'up' && <i className="ri-arrow-up-line text-green-500 text-xs"></i>}
                            {detail.trend === 'down' && <i className="ri-arrow-down-line text-red-500 text-xs"></i>}
                            {detail.trend === 'stable' && <i className="ri-subtract-line text-gray-400 text-xs"></i>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">주요 특이사항</h5>
                    <p className="text-xs text-gray-600 leading-relaxed">{category.notes}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
